import { createFileRoute } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, AlertCircle, RefreshCw, Database, Server, Globe, Shield, Zap, Activity } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';

export const Route = createFileRoute('/status')({
  component: StatusPage,
});

interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  uptime: number;
  version: string;
  checks?: {
    database?: {
      status: 'healthy' | 'unhealthy';
      latency?: number;
    };
  };
}

type ServiceStatus = 'operational' | 'degraded' | 'outage' | 'checking';

interface Service {
  name: string;
  description: string;
  status: ServiceStatus;
  latency?: number;
  icon: React.ComponentType<{ className?: string }>;
}

function StatusIcon({ status, size = 'md' }: { status: ServiceStatus; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  if (status === 'operational') return <CheckCircle2 className={`${sizeClass} text-green-500`} />;
  if (status === 'degraded') return <AlertCircle className={`${sizeClass} text-yellow-500`} />;
  if (status === 'outage') return <XCircle className={`${sizeClass} text-red-500`} />;
  return <RefreshCw className={`${sizeClass} text-blue-500 animate-spin`} />;
}

function StatusBadge({ status }: { status: ServiceStatus }) {
  const config = {
    operational: { label: 'Operational', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
    degraded: { label: 'Degraded', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
    outage: { label: 'Outage', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
    checking: { label: 'Checking...', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
  };
  const { label, bg, text } = config[status];
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${bg} ${text}`}>
      <StatusIcon status={status} size="sm" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function StatusPage() {
  const [services, setServices] = useState<Service[]>([
    { name: 'Web Application', description: 'Frontend application', status: 'operational', icon: Globe },
    { name: 'API Gateway', description: 'Core API endpoints', status: 'checking', icon: Server },
    { name: 'Database Cluster', description: 'PostgreSQL database', status: 'checking', icon: Database },
    { name: 'Repository Analysis', description: 'Code analysis engine', status: 'checking', icon: Zap },
    { name: 'Security Scanning', description: 'Vulnerability detection', status: 'checking', icon: Shield },
    { name: 'Real-time Updates', description: 'WebSocket connections', status: 'checking', icon: Activity },
  ]);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [apiHealth, setApiHealth] = useState<HealthCheck | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const checkHealth = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // eslint-disable-next-line dashboard/no-raw-fetch -- Health check uses different base path and must work before auth
      const response = await fetch('/api/status/readyz');
      if (!response.ok) throw new Error('API unhealthy');
      const data: HealthCheck = await response.json();
      setApiHealth(data);

      setServices(prev => prev.map(service => {
        if (service.name === 'Web Application') return service; // Already operational
        if (service.name === 'API Gateway') {
          return { ...service, status: data.status === 'healthy' ? 'operational' : 'degraded' };
        }
        if (service.name === 'Database Cluster') {
          return {
            ...service,
            status: data.checks?.database?.status === 'healthy' ? 'operational' : 'outage',
            latency: data.checks?.database?.latency,
          };
        }
        // Other services inherit API status
        return { ...service, status: data.status === 'healthy' ? 'operational' : 'degraded' };
      }));
      setLastChecked(new Date());
    } catch {
      setServices(prev => prev.map(service =>
        service.name === 'Web Application' ? service : { ...service, status: 'outage' }
      ));
      setLastChecked(new Date());
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [checkHealth]);

  const overallStatus: ServiceStatus = services.every(s => s.status === 'operational')
    ? 'operational'
    : services.some(s => s.status === 'outage')
    ? 'outage'
    : services.some(s => s.status === 'checking')
    ? 'checking'
    : 'degraded';

  const overallMessage = {
    operational: 'All Systems Operational',
    degraded: 'Partial System Degradation',
    outage: 'System Outage Detected',
    checking: 'Checking System Status...',
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">System Status</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">ForgeNetwork Status</h1>
            <p className="text-muted-foreground text-lg mb-8">
              Real-time operational status of ForgeNetwork services
            </p>
            <StatusBadge status={overallStatus} />
          </div>

          {/* Overall Status Card */}
          <Card className={`mb-8 ${
            overallStatus === 'operational' ? 'border-green-500/30' :
            overallStatus === 'degraded' ? 'border-yellow-500/30' :
            overallStatus === 'outage' ? 'border-red-500/30' : 'border-blue-500/30'
          }`}>
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <StatusIcon status={overallStatus} size="lg" />
                  <div>
                    <h2 className="text-xl font-bold">{overallMessage[overallStatus]}</h2>
                    {lastChecked && (
                      <p className="text-sm text-muted-foreground">
                        Last checked: {lastChecked.toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </div>
                <Button variant="outline" onClick={checkHealth} disabled={isRefreshing}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Services List */}
          <div className="bg-card rounded-xl border p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6">Core Services</h2>
            <div className="space-y-4">
              {services.map(service => {
                const Icon = service.icon;
                return (
                  <div key={service.name} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{service.name}</span>
                        <p className="text-sm text-muted-foreground">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {service.latency !== undefined && (
                        <span className="text-sm text-muted-foreground">{service.latency}ms</span>
                      )}
                      <StatusIcon status={service.status} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* System Metrics */}
          {apiHealth && (
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-card rounded-lg border p-6 text-center">
                <p className="text-3xl font-bold text-primary mb-1">{formatUptime(apiHealth.uptime)}</p>
                <p className="text-sm text-muted-foreground">API Uptime</p>
              </div>
              <div className="bg-card rounded-lg border p-6 text-center">
                <p className="text-3xl font-bold text-primary mb-1">v{apiHealth.version}</p>
                <p className="text-sm text-muted-foreground">API Version</p>
              </div>
              <div className="bg-card rounded-lg border p-6 text-center">
                <p className="text-3xl font-bold text-primary mb-1">
                  {apiHealth.checks?.database?.latency ? `${apiHealth.checks.database.latency}ms` : 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">Database Latency</p>
              </div>
            </div>
          )}

          {/* SLA Targets */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-card rounded-lg border p-6 text-center">
              <p className="text-3xl font-bold text-primary mb-1">99.9%</p>
              <p className="text-sm text-muted-foreground">Uptime SLA</p>
            </div>
            <div className="bg-card rounded-lg border p-6 text-center">
              <p className="text-3xl font-bold text-primary mb-1">&lt;100ms</p>
              <p className="text-sm text-muted-foreground">API Response Target</p>
            </div>
            <div className="bg-card rounded-lg border p-6 text-center">
              <p className="text-3xl font-bold text-primary mb-1">24/7</p>
              <p className="text-sm text-muted-foreground">Monitoring</p>
            </div>
          </div>

          {/* Recent Incidents */}
          <div className="bg-card rounded-xl border p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6">Recent Incidents</h2>
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="font-medium">No recent incidents</p>
              <p className="text-sm text-muted-foreground">All systems operating normally</p>
            </div>
          </div>

          {/* Contact */}
          <p className="text-center text-muted-foreground">
            Experiencing issues? Contact us at{' '}
            <a href="mailto:support@forgenetwork.pro" className="text-primary hover:underline">
              support@forgenetwork.pro
            </a>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
